import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * Dialog shown after a successful add-to-cart from the parent product form.
 *
 * @extends {DialogComponent}
 */
class AddToCartDialogComponent extends DialogComponent {
  /** @type {number} */
  #currentStep = 0;

  #handleCartAdd = (event) => {
    if (!(event instanceof CartAddEvent)) return;
    if (event.detail.data?.didError) return;
    if (event.detail.data?.source !== 'product-form-component') return;

    const dialogProductId = this.dataset.productId;
    const eventProductId = event.detail.data?.productId;

    if (dialogProductId == null || eventProductId == null) return;
    if (String(dialogProductId) !== String(eventProductId)) return;

    this.showDialog();
  };

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  showDialog() {
    super.showDialog();
    this.#initSteps();
  }

  async closeDialog() {
    await super.closeDialog();
    this.#currentStep = 0;
  }

  /** @param {MouseEvent} [_event] */
  async openCart(_event) {
    await this.closeDialog();
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
    } else {
      window.location.href = /** @type {any} */ (window.Shopify)?.routes?.cart_url || '/cart';
    }
  }

  /** @param {MouseEvent} [_event] */
  goNextStep(_event) {
    const steps = this.#getSteps();
    if (!steps.length) return;

    const nextIndex = this.#currentStep + 1;
    if (nextIndex >= steps.length) return;

    steps[this.#currentStep]?.setAttribute('hidden', '');
    this.#currentStep = nextIndex;
    const active = steps[this.#currentStep];
    active?.removeAttribute('hidden');
    active?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    this.#updateNextStepButton();
  }

  #getSteps() {
    const container = this.querySelector('.add-to-cart-dialog__steps');
    if (!container) return [];

    return [...container.children].filter((child) => child instanceof HTMLElement);
  }

  #initSteps() {
    const steps = this.#getSteps();
    this.#currentStep = 0;

    steps.forEach((step, index) => {
      step.classList.add('add-to-cart-dialog__step');
      if (index === 0) {
        step.removeAttribute('hidden');
      } else {
        step.setAttribute('hidden', '');
      }
    });

    this.#updateNextStepButton();
  }

  #updateNextStepButton() {
    const nextButton = this.querySelector('.add-to-cart-dialog__footer-next');
    if (!(nextButton instanceof HTMLButtonElement)) return;

    const steps = this.#getSteps();
    const isLastStep = steps.length === 0 || this.#currentStep >= steps.length - 1;
    nextButton.hidden = steps.length <= 1;
    nextButton.disabled = isLastStep;
  }
}

if (!customElements.get('add-to-cart-dialog-component')) {
  customElements.define('add-to-cart-dialog-component', AddToCartDialogComponent);
}
